import { useEffect } from "react";
import { architectureImage, bookCoverImage, portraitImage, publicLinks } from "@/data/siteData";

export const SITE_URL = "https://ashwinipatil.ai";
const PERSON_ID = `${SITE_URL}/#person`;
const WEBSITE_ID = `${SITE_URL}/#website`;

export const personSchema = {
  "@type": "Person",
  "@id": PERSON_ID,
  name: "Ashwini Patil",
  url: `${SITE_URL}/about`,
  image: portraitImage,
  description: "Data, Analytics & Decision Intelligence leader working across enterprise data, technology transformation, and decisions people can understand, trust, and improve.",
  jobTitle: "Data, Analytics & Decision Intelligence leader",
  knowsAbout: [
    "Enterprise Data & AI Transformation",
    "Enterprise AI",
    "Decision Intelligence",
    "Data & Analytics Modernization",
    "Enterprise Data Architecture",
    "Semantic Foundations",
    "AI Strategy and Governance",
    "Decision Systems",
    "Analytics Modernization",
    "Technology Transformation",
    "Microsoft Fabric",
    "Databricks",
    "Power BI",
    "Agentic AI",
  ],
  sameAs: [publicLinks.linkedin, publicLinks.bookPurchase],
};

const pageDefinitions = {
  "/": {
    title: "Ashwini Patil | Enterprise AI & Decision Intelligence",
    description: "Ashwini Patil writes and speaks about Enterprise AI, Decision Intelligence, data modernization, and the architecture that helps leaders make better decisions.",
    type: "home",
    name: "Ashwini Patil",
  },
  "/about": {
    title: "Ashwini Patil | Enterprise Data & AI Transformation",
    description: "Meet Ashwini Patil, an Enterprise Data & AI transformation leader across data architecture, analytics modernization, AI governance, and Decision Intelligence.",
    type: "profile",
    name: "About Ashwini Patil",
  },
  "/insights": {
    title: "Enterprise AI & Data Strategy Insights | Ashwini Patil",
    description: "Read Ashwini Patil's perspectives on Enterprise AI, data strategy, analytics modernization, semantic foundations, governance, and decisions.",
    type: "collection",
    name: "Insights",
  },
  "/frameworks": {
    title: "Enterprise Decision Intelligence | Ashwini Patil",
    description: "Explore Ashwini Patil's working models for Decision Intelligence, semantic foundations, governed AI, accountable action, and outcome learning.",
    type: "page",
    name: "Decision Intelligence",
  },
  "/transformations": {
    title: "Data & Analytics Modernization | Ashwini Patil",
    description: "Explore anonymized transformation patterns from Ashwini Patil's work across data, analytics modernization, architecture, governance, and AI-enabled workflows.",
    type: "collection",
    name: "Transformations",
  },
  "/book": {
    title: "The Decision Gap by Ashwini Patil | Data & Decisions",
    description: "The Decision Gap by Ashwini Patil examines why better data, analytics, and AI can still leave important enterprise decisions difficult.",
    type: "book",
    name: "Published Books",
    image: bookCoverImage,
  },
  "/speaking": {
    title: "Enterprise AI & Data Speaking | Ashwini Patil",
    description: "Invite Ashwini Patil to speak about Enterprise AI, data transformation, Decision Intelligence, semantic architecture, governance, and human judgment.",
    type: "page",
    name: "Speaking & Conversations",
  },
  "/connect": {
    title: "Connect with Ashwini Patil | Data & AI Transformation",
    description: "Connect with Ashwini Patil about Enterprise AI, Data & AI transformation, Decision Intelligence, speaking, writing, and thoughtful enterprise questions.",
    type: "contact",
    name: "Connect",
  },
  "/articles": {
    title: "Articles & External Writing | Ashwini Patil",
    description: "Read external essays by Ashwini Patil on Medium and other publications covering data, Enterprise AI, Decision Intelligence, and better enterprise decisions.",
    type: "collection",
    name: "Articles",
  },
  "/decision-ledger": {
    title: "Decision Ledger | Ashwini Patil",
    description: "Use Ashwini Patil's Decision Ledger to examine context, design, memory, outcomes, learning, and adaptation around an important decision.",
    type: "page",
    name: "Decision Ledger",
  },
};

function normalizePath(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "");
}

function canonicalFor(pathname) {
  const normalized = normalizePath(pathname);
  return `${SITE_URL}${normalized === "/" ? "/" : normalized}`;
}

function breadcrumbSchema(items) {
  return {
    "@type": "BreadcrumbList",
    "@id": `${items[items.length - 1].url}#breadcrumb`,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

function pageSchema({ canonical, title, description, type }) {
  const schemaType = type === "profile" ? "ProfilePage" : type === "collection" ? "CollectionPage" : type === "contact" ? "ContactPage" : "WebPage";
  return {
    "@type": schemaType,
    "@id": `${canonical}#webpage`,
    url: canonical,
    name: title,
    description,
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": PERSON_ID },
    inLanguage: "en",
  };
}

function homeSchema() {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: `${SITE_URL}/`,
    name: "Ashwini Patil",
    alternateName: "Ashwini Patil — Enterprise AI & Decision Intelligence",
    inLanguage: "en",
  };
}

function bookSchema(canonical, description) {
  return {
    "@type": "Book",
    "@id": `${canonical}#book`,
    url: canonical,
    name: "The Decision Gap",
    author: { "@id": PERSON_ID },
    image: bookCoverImage,
    description,
    inLanguage: "en",
  };
}

function pageBreadcrumb(pathname, name) {
  return breadcrumbSchema([
    { name: "Home", url: `${SITE_URL}/` },
    { name, url: canonicalFor(pathname) },
  ]);
}

export function getRouteSeo(pathname) {
  const normalized = normalizePath(pathname);
  if (normalized.startsWith("/insights/") && normalized !== "/insights/manage") return null;
  if (normalized === "/login" || normalized === "/register" || normalized === "/forgot-password" || normalized === "/auth/callback" || normalized === "/auth/reset-password" || normalized === "/insights/manage" || normalized.startsWith("/decision-ledger/snapshot/")) {
    return {
      title: "Private area | Ashwini Patil",
      description: "Private editorial and decision workspace.",
      canonical: canonicalFor(normalized),
      image: architectureImage,
      noIndex: true,
      schemas: [],
    };
  }

  const definition = pageDefinitions[normalized];
  if (!definition) {
    return {
      title: "Page not found | Ashwini Patil",
      description: "The requested page could not be found on Ashwini Patil's website.",
      canonical: canonicalFor(normalized),
      image: architectureImage,
      noIndex: true,
      schemas: [],
    };
  }

  const canonical = canonicalFor(normalized);
  const schemas = [pageSchema({ canonical, title: definition.title, description: definition.description, type: definition.type })];
  if (normalized === "/") {
    schemas.unshift(homeSchema());
    schemas.push(personSchema);
  }
  if (definition.type === "book") schemas.push(bookSchema(canonical, definition.description));
  if (normalized !== "/") schemas.push(pageBreadcrumb(normalized, definition.name));
  if (definition.type === "profile") schemas[0] = { ...schemas[0], mainEntity: { "@id": PERSON_ID } };

  return {
    title: definition.title,
    description: definition.description,
    canonical,
    image: definition.image || architectureImage,
    type: normalized === "/" ? "website" : "website",
    noIndex: false,
    schemas,
  };
}

const monthNumbers = { JAN: "01", FEB: "02", MAR: "03", APR: "04", MAY: "05", JUN: "06", JUL: "07", AUG: "08", SEP: "09", OCT: "10", NOV: "11", DEC: "12" };

function isoDate(value) {
  if (!value) return undefined;
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const match = text.match(/(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})/);
  if (match) return `${match[3]}-${monthNumbers[match[2].toUpperCase()] || "01"}-${String(match[1]).padStart(2, "0")}`;
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString().slice(0, 10);
}

export function getArticleSeo(article, slug) {
  const canonical = canonicalFor(`/insights/${slug}`);
  const published = isoDate(article.datePublished || article.date_published || article.created_at || article.date);
  const modified = isoDate(article.dateModified || article.date_modified || article.updated_at) || published;
  const description = article.excerpt || `An Insight by Ashwini Patil on ${article.category || "Enterprise Data & AI transformation"}.`;
  const title = `${article.title} | Ashwini Patil`;
  const articleSchema = {
    "@type": "BlogPosting",
    "@id": `${canonical}#article`,
    mainEntityOfPage: { "@id": canonical },
    headline: article.title,
    description,
    url: canonical,
    image: [architectureImage],
    author: { "@id": PERSON_ID },
    publisher: { "@id": PERSON_ID },
    articleSection: article.category,
    inLanguage: "en",
    ...(published ? { datePublished: published } : {}),
    ...(modified ? { dateModified: modified } : {}),
  };
  return {
    title,
    description,
    canonical,
    image: architectureImage,
    type: "article",
    noIndex: false,
    schemas: [articleSchema, breadcrumbSchema([
      { name: "Home", url: `${SITE_URL}/` },
      { name: "Insights", url: `${SITE_URL}/insights` },
      { name: article.title, url: canonical },
    ])],
  };
}

function setTitle(value) {
  const matches = Array.from(document.head.querySelectorAll("title"));
  const tag = matches[0] || document.createElement("title");
  tag.textContent = value;
  tag.setAttribute("data-seo-managed", "true");
  if (!tag.parentNode) document.head.appendChild(tag);
  matches.slice(1).forEach((duplicate) => duplicate.remove());
}

function setMeta(attribute, key, content) {
  if (!content) return;
  const selector = `meta[${attribute}="${key}"]`;
  const matches = Array.from(document.head.querySelectorAll(selector));
  const tag = matches[0] || document.createElement("meta");
  tag.setAttribute(attribute, key);
  tag.setAttribute("content", content);
  tag.setAttribute("data-seo-managed", "true");
  if (!tag.parentNode) document.head.appendChild(tag);
  matches.slice(1).forEach((duplicate) => duplicate.remove());
}

function setCanonical(url) {
  const links = Array.from(document.head.querySelectorAll("link[rel=canonical]"));
  const link = links[0] || document.createElement("link");
  link.setAttribute("rel", "canonical");
  link.setAttribute("href", url);
  link.setAttribute("data-seo-managed", "true");
  if (!link.parentNode) document.head.appendChild(link);
  links.slice(1).forEach((duplicate) => duplicate.remove());
}

export default function SeoHead({ title, description, canonical, image, type = "website", noIndex = false, schemas = [] }) {
  const serializedSchemas = JSON.stringify(schemas);

  useEffect(() => {
    setTitle(title);
    setMeta("name", "description", description);
    setMeta("name", "author", "Ashwini Patil");
    setMeta("name", "robots", noIndex ? "noindex,nofollow,noarchive" : "index,follow");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", type);
    setMeta("property", "og:site_name", "Ashwini Patil");
    setMeta("property", "og:locale", "en_US");
    setMeta("property", "og:image", image);
    setMeta("property", "og:image:alt", "Ashwini Patil — Enterprise Data & AI transformation and Decision Intelligence");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);
    setCanonical(canonical);

    document.querySelectorAll("script[data-seo-jsonld]").forEach((script) => script.remove());
    const schemaList = JSON.parse(serializedSchemas);
    if (schemaList.length) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seoJsonld = "true";
      script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": schemaList });
      document.head.appendChild(script);
    }
  }, [canonical, description, image, noIndex, serializedSchemas, title, type]);

  return null;
}
